import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  Spinner,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import ItemService from "../../../Services/QRCodeServices/MenuItem/ItemService";
import axios from "axios";
import CategoryService from "../../../Services/QRCodeServices/MenuCategory/CategoryService";
import CurrencyService from "../../../Services/QRCodeServices/CurrencyService/CurrencyService";
import UnitService from "../../../Services/QRCodeServices/UnitService/UnitService";
import { ORG_MENUITEMS_UPDATE } from "../../../api/endpoints";
import toast from "react-hot-toast";

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const EditMenuItem = ({
  isOpen,
  toggleModal,
  data,
  refreshData,
  allData,
  fetchCategoryData,
}) => {
  console.log("🚀 ~ data:", data);
  console.log("🚀 ~ allData:", allData);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [existingImage, setExistingImage] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));

  // Initial values using the existing menu item's data
  const initialValues = {
    title: data?.title || "",
    description: data?.description || "",
    ingredients: data?.ingredients || "",
    unitId: data?.unitId || "",
    currency: data?.currencyId || "",
    price: data?.price || "0",
    recipePrice: data?.recipePrice || "0",
    menuCategoryId: data?.menuCategoryId || "",
    MenuAttributes: data?.menuAttributes || [],
    isPurchase: data?.isPurchase || false,
    hasStock: data?.hasStock || false,
    isSpecial: data?.isSpecial || false,
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
    } else if (data?.cloudR2ImagePath) {
      setSelectedFiles([
        {
          preview: data.cloudR2ImagePath.getCDNUrl(),
          existing: true,
        },
      ]);
    }
  }, [isOpen, data]);

  const getValidationSchema = (values) => {
    const baseSchema = {
      title: Yup.string().required("Title is required"),
      description: Yup.string(),
      ingredients: Yup.string(),
      unitId: Yup.string().required("Unit is required"),
      image: Yup.mixed().nullable(),
      menuCategoryId: Yup.string(),
      currency: Yup.string().required("Currency is required"),
      MenuAttributes: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required("Item name is required"),
          rate: Yup.number().required("Item rate is required"),
        })
      ),
    };

    if (values.MenuAttributes.length === 0) {
      baseSchema.price = Yup.number().required("Price is required");
    }

    return Yup.object().shape(baseSchema);
  };

  const fetchAllUnits = async () => {
    let allUnits = [];
    let pageNumber = 1;
    const pageSize = 50;
    let hasMoreData = true;

    while (hasMoreData) {
      const response = await UnitService.getList({ pageNumber, pageSize });
      allUnits = [...allUnits, ...response.data];
      hasMoreData = response.data.length === pageSize;
      pageNumber++;
    }
    return allUnits;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await CategoryService.getList();
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    const fetchCurrency = async () => {
      try {
        const response = await CurrencyService.getList();
        setCurrencyOptions(response.data);
      } catch (error) {
        console.error("Error fetching currency:", error);
      }
    };
    const fetchUnit = async () => {
      try {
        const allUnits = await fetchAllUnits();
        const options = allUnits.map((item) => ({
          value: item.id,
          label: item.title,
        }));
        setUnitOptions(options);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchCurrency();
    fetchUnit();
    fetchCategories();

    if (data?.image) {
      setExistingImage(data.image);
    }
  }, [data]);

  const handleAcceptedFiles = (files, setFieldValue) => {
    const formattedFiles = files.map((file) => ({
      ...file,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    }));

    setSelectedFiles([...formattedFiles]);
    setExistingImage(null);

    if (files.length > 0) {
      setFieldValue("image", files[0]);
    }
  };

  const handleFileRemove = (indexToRemove, setFieldValue) => {
    setSelectedFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (newFiles.length === 0) {
        setFieldValue("image", null);
      }
      return newFiles;
    });
  };

  const handleRemoveAttribute = (index, values, setFieldValue) => {
    const newAttributes = values.MenuAttributes.filter((_, i) => i !== index);
    setFieldValue("MenuAttributes", newAttributes);
    if (newAttributes.length === 0) {
      setFieldValue("price", values.price || "0");
    }
  };

  const handleAddAttribute = (values, setFieldValue) => {
    setFieldValue("MenuAttributes", [
      ...values.MenuAttributes,
      { name: "", rate: 0 },
    ]);
    if (values.MenuAttributes.length === 0) {
      setFieldValue("price", "0");
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("ingredients", values.ingredients);
      formData.append("unitId", values.unitId);
      formData.append("menuCategoryId", values.menuCategoryId);
      formData.append(
        "price",
        values.MenuAttributes.length > 0 ? "0" : values.price
      );
      formData.append("currencyId", values.currency);
      formData.append("recipePrice", values.recipePrice);
      formData.append("isPurchase", values.isPurchase);
      formData.append("hasStock", values.hasStock);
      formData.append("isSpecial", values.isSpecial);

      if (values.image) {
        formData.append("image", values.image);
      }

      values.MenuAttributes.forEach((attribute, index) => {
        formData.append(
          `MenuAttributesDto.MenuAttributes[${index}].name`,
          attribute.name
        );
        formData.append(
          `MenuAttributesDto.MenuAttributes[${index}].rate`,
          attribute.rate
        );
      });

      const response = await axios.put(
        ORG_MENUITEMS_UPDATE(data.id),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      );

      setTimeout(() => {
        toast.success("Item updated successfully");
        refreshData();
        setLoading(false);
        toggleModal();
      }, 2500);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred during submission. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg">
      <ModalHeader toggle={toggleModal}>Edit Menu Item</ModalHeader>
      <ModalBody>
        <Formik
          initialValues={initialValues}
          validate={(values) => {
            const schema = getValidationSchema(values);
            try {
              schema.validateSync(values, { abortEarly: false });
              return {};
            } catch (err) {
              return err.inner.reduce((acc, error) => {
                acc[error.path] = error.message;
                return acc;
              }, {});
            }
          }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Dropzone
                      onDrop={(acceptedFiles) =>
                        handleAcceptedFiles(acceptedFiles, setFieldValue)
                      }
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div className="dropzone dz-clickable pt-3">
                          <div
                            className="dz-message needsclick mb-5 p-3"
                            {...getRootProps()}
                          >
                            <i className="align-item-center display-4 text-muted ri-upload-cloud-2-fill" />
                            <input {...getInputProps()} />
                            <h6 className="fs-15 fw-semi-bold pt-3">
                              Drop files here or{" "}
                              <Link to="#">click to upload.</Link>
                            </h6>

                            {existingImage && !selectedFiles.length && (
                              <Card className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                                <Row className="align-items-center">
                                  <Col className="col-auto">
                                    <img
                                      style={{
                                        width: "5rem",
                                        height: "3rem",
                                        objectFit: "cover",
                                      }}
                                      className="avatar-sm rounded bg-light"
                                      alt="Existing menu item"
                                      src={
                                        existingImage.startsWith("data:")
                                          ? existingImage
                                          : `data:image/jpeg;base64,${existingImage}`
                                      }
                                    />
                                  </Col>
                                  <Col>
                                    <p className="mb-0 text-muted fs-6">
                                      Existing Image
                                    </p>
                                  </Col>
                                </Row>
                              </Card>
                            )}

                            {selectedFiles.map((f, i) => (
                              <Card
                                key={i}
                                className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                              >
                                <Row className="align-items-center">
                                  <Col className="col-auto">
                                    <img
                                      height="100"
                                      className="avatar-sm rounded bg-light"
                                      alt={f.name}
                                      src={f.preview}
                                    />
                                  </Col>
                                  <Col>
                                    <Link
                                      to="#"
                                      className="text-muted font-weight-bold"
                                    >
                                      {f.name}
                                    </Link>
                                    <p className="mb-0 fs-12">
                                      {f.formattedSize}
                                    </p>
                                  </Col>
                                  <Col lg={2}>
                                    <Link
                                      onClick={() =>
                                        handleFileRemove(i, setFieldValue)
                                      }
                                    >
                                      <h6>
                                        <i className="ri-delete-bin-2-line text-danger"></i>
                                      </h6>
                                    </Link>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </Dropzone>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="title">
                          Title <span className="text-danger">*</span>
                        </Label>
                        <Field
                          name="title"
                          type="text"
                          className="form-control"
                          id="title"
                        />
                        <ErrorMessage
                          name="title"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="description">Description</Label>
                        <Field
                          name="description"
                          type="text"
                          className="form-control"
                          id="description"
                        />
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Menu Attributes</Label>
                        {values.MenuAttributes.map((attr, index) => (
                          <Row key={index} className="align-items-center mb-3">
                            <Col md={5}>
                              <Field
                                name={`MenuAttributes[${index}].name`}
                                type="text"
                                className="form-control"
                                placeholder="Item Name"
                              />
                              <ErrorMessage
                                name={`MenuAttributes[${index}].name`}
                                component="div"
                                className="text-danger"
                              />
                            </Col>
                            <Col md={5}>
                              <Field
                                name={`MenuAttributes[${index}].rate`}
                                type="number"
                                className="form-control"
                                placeholder="Attribute Rate"
                              />
                              <ErrorMessage
                                name={`MenuAttributes[${index}].rate`}
                                component="div"
                                className="text-danger"
                              />
                            </Col>
                            <Col md={2} className="text-end">
                              <Button
                                color="danger"
                                className="btn-sm"
                                onClick={() =>
                                  handleRemoveAttribute(
                                    index,
                                    values,
                                    setFieldValue
                                  )
                                }
                              >
                                <i className="ri-delete-bin-2-line"></i>
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Button
                          color="primary"
                          className="btn-sm ms-2"
                          onClick={() =>
                            handleAddAttribute(values, setFieldValue)
                          }
                        >
                          +
                        </Button>
                      </FormGroup>
                    </Col>

                    {values.MenuAttributes.length === 0 && (
                      <>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="price">
                              Price <span className="text-danger">*</span>
                            </Label>
                            <Field
                              name="price"
                              type="number"
                              className="form-control"
                              id="price"
                            />
                            <ErrorMessage
                              name="price"
                              component="div"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="recipePrice">
                              Recipe Price{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Field
                              name="recipePrice"
                              type="number"
                              className="form-control"
                              id="recipePrice"
                            />
                            <ErrorMessage
                              name="recipePrice"
                              component="div"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </>
                    )}

                    <Col md={6}>
                      <FormGroup>
                        <Label for="currency">
                          Currency <span className="text-danger">*</span>
                        </Label>
                        <Field
                          name="currency"
                          as="select"
                          className="form-control"
                          id="currency"
                        >
                          <option value="" disabled>
                            Select Currency
                          </option>
                          {currencyOptions?.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="currency"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label for="unitId">
                          Unit <span className="text-danger">*</span>
                        </Label>
                        <Field
                          name="unitId"
                          as="select"
                          className="form-control"
                          id="unitId"
                        >
                          <option value="" disabled>
                            Select Unit
                          </option>
                          {unitOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="unitId"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label for="menuCategoryId">
                          Menu Category <span className="text-danger">*</span>
                        </Label>
                        <Field
                          name="menuCategoryId"
                          as="select"
                          className="form-control"
                          id="menuCategoryId"
                        >
                          <option value="" disabled>
                            Select Category
                          </option>
                          {allData?.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="menuCategoryId"
                          component="div"
                          className="text-danger"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <div className="form-check">
                          <Field
                            type="checkbox"
                            name="isPurchase"
                            className="form-check-input"
                            id="isPurchase"
                          />
                          <Label className="form-check-label" for="isPurchase">
                            Is Purchase
                          </Label>
                        </div>
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <div className="form-check">
                          <Field
                            type="checkbox"
                            name="hasStock"
                            className="form-check-input"
                            id="hasStock"
                          />
                          <Label className="form-check-label" for="hasStock">
                            Has Stock
                          </Label>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <div className="form-check">
                          <Field
                            type="checkbox"
                            name="isSpecial"
                            className="form-check-input"
                            id="isSpecial"
                          />
                          <Label className="form-check-label" for="isSpecial">
                            Special
                          </Label>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <ModalFooter>
                <Button color="danger" onClick={toggleModal}>
                  Cancel
                </Button>
                <Button type="submit" color="success" disabled={loading}>
                  {loading && (
                    <Spinner size="sm" className="me-2">
                      Loading...
                    </Spinner>
                  )}{" "}
                  {loading ? "Updating..." : "Update"}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default EditMenuItem;
